<script>
/**
 * Cartão de suporte da `/home-ia`. Duas casas, as duas na superfície imersiva:
 * abaixo do composer no ESTADO VAZIO (`view-chat.vue`, prop
 * `com-cartao-de-suporte`) e no PÉ do painel da conversa, depois da seção
 * Contexto (`painel-do-chat/painel-flutuante.vue`) — lá na variante
 * `compacto`, porque a coluna do painel é estreita.
 *
 * ★ Modelado no cartão "Seu advisor" da referência (print do usuário,
 * 17/09/2026): pílula muito quieta na superfície quente clara, ~48px de altura
 * e pouco menos da METADE da largura do composer, centrada; dentro, o glifo do
 * WhatsApp à esquerda, duas linhas empilhadas (rótulo minúsculo em cima, nome
 * um pouco mais forte embaixo) e um chevron discreto colado na borda direita.
 * Sem borda, sem sombra, sem cor de destaque — a única cor é o verde da MARCA
 * do WhatsApp no ícone, que fica DIRETO sobre a superfície do cartão: a
 * pastilha atrás dele foi desenhada e recusada (o quadrado colorido virava o
 * elemento mais pesado de uma peça que tem de ser quieta).
 *
 * ⚠️ Este é um produto de wealth management: o cartão tem de ler como
 * afordância silenciosa, nunca como botão de promoção. Quatro versões foram
 * recusadas no MESMO dia e não devem voltar: (1) cartão alto na cor da navbar
 * ESCURA da plataforma (`--bg-navbar`); (2) a mesma pílula com a tipografia de
 * 11/13px da `.ia-sidebar-row`, ainda grande demais; (3) a pílula com hairline
 * em volta; (4) o glifo dentro de uma pastilha. Em dúvida: menos contraste,
 * menos peso, mais respiro.
 *
 * O CARTÃO INTEIRO é o alvo — daí o root ser o próprio `<a>`, e não um `div`
 * com um link dentro: só assim o clique em qualquer ponto vale e o foco por
 * teclado é um só.
 *
 * ⚠️ Ele SUBSTITUI o link "Suporte no WhatsApp" que morava na linha do hint do
 * composer até 17/09/2026 (componente `suporte-whatsapp.vue`, apagado) — não
 * convive com ele: o mesmo destino duas vezes na tela era o defeito que este
 * cartão veio resolver.
 */
import { SUPORTE_WHATSAPP } from '@modules/aawz-intelligence-v2/constants/Text'
import { linkDoSuporte } from './link-do-suporte'

export default {
  name: 'IntelligenceV2SuporteWhatsappCard',
  props: {
    // Variante do PAINEL da conversa: a coluna dele tem 200-240px, contra os
    // 672px da coluna do estado vazio. Sai o teto de largura e a centralização
    // (ali o cartão ocupa a linha inteira, como as demais seções) e o glifo
    // encolhe. É uma prop, e não um segundo componente, porque o conteúdo, o
    // destino e a anatomia são exatamente os mesmos.
    compacto: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return { suporte: SUPORTE_WHATSAPP, corDaMarca: SUPORTE_WHATSAPP.corDaMarca }
  },
  computed: {
    link() {
      return linkDoSuporte()
    },
  },
}
</script>

<template>
  <a
    :href="link"
    target="_blank"
    rel="noopener noreferrer"
    class="suporte-card d-flex align-center"
    :class="{ 'suporte-card--compacto': compacto }"
    data-test="suporte-whatsapp-card"
  >
    <icon-svg
      name="whatsapp"
      :size="compacto ? 14 : 16"
      :color="corDaMarca"
      class="flex-shrink-0"
    />

    <span class="suporte-card__texto d-flex flex-column">
      <span class="suporte-card__chamada ia-texto-micro">{{ suporte.chamada }}</span>
      <span class="suporte-card__nome ia-texto-auxiliar">{{ suporte.rotulo }}</span>
    </span>

    <icon-svg
      name="chevron-right"
      :size="12"
      color="var(--neutral-600)"
      class="suporte-card__chevron flex-shrink-0 ml-auto"
    />
  </a>
</template>

<style lang="sass" scoped>
// ⚠️ 10px e 12px são passos fora da escala do DS, e `design/_ia-sidebar.sass`
// reivindica os dela (11/12/13) "em nenhum `<style scoped>`". A regra existe
// para não DUPLICAR o mesmo passo entre componentes: aqui a receita tem um
// consumidor só (este cartão, nas duas variantes) e nada a duplicar, então ela
// fica junto do componente em vez de engordar a partial da sidebar, que é de
// outra anatomia (linha de lista de uma linha só).
.suporte-card
  // Superfície quente da lateral da Home IA (#F4F4F4) sobre o fundo do
  // conteúdo (`--ia-content-bg`, #FCFCFC). A diferença é sutil DE PROPÓSITO —
  // na referência o cartão quase se dissolve na página. Sem borda e sem
  // sombra: a superfície sozinha já o separa.
  background: var(--sidebar-warm-bg)
  // Raio generoso, na mesma família do composer logo acima.
  border-radius: var(--radius-lg)
  // ~43% da coluna do estado vazio (`--ia-chat-trilho-largura-vazio`, 672px) —
  // a proporção medida na referência (cartão 248px / composer 580px).
  max-width: 290px
  // Só a centralização HORIZONTAL mora aqui. O vão até o composer é do
  // RODAPÉ da coluna do estado vazio (`empty-messages.vue`), que centra o
  // cartão no espaço livre abaixo do composer — um `margin-top` fixo aqui
  // somaria ao vão e desequilibraria os dois lados.
  margin: 0 auto
  padding: 8px 10px
  gap: 10px
  text-decoration: none
  transition: background var(--motion-duration-fast) var(--motion-easing-standard)

  // Hover que se SENTE, não que se vê: a superfície sobe na direção do branco
  // em vez de descer para `--sidebar-warm-hover` (#E7E7E7), que num cartão
  // deste tamanho lia como um degrau de cinza. #F4F4F4 → ~#F8F8F8.
  &:hover,
  &:focus-visible
    background: color-mix(in srgb, var(--sidebar-warm-bg) 60%, white)

  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: 2px

.suporte-card__texto
  min-width: 0

// A tipografia da chamada e do nome (família, 10/12px, entrelinha, peso, cor)
// vem das classes por papel de `design/_ia-texto.sass`, no template.
.suporte-card__nome
  color: var(--neutral-900)
  overflow: hidden
  text-overflow: ellipsis
  white-space: nowrap

.suporte-card--compacto
  max-width: none
  margin: 0
  padding: 6px 8px
  gap: 8px
  border-radius: var(--radius-sm)

// A cor do papel (leva 2 da escala: a classe `ia-texto-*` não carrega cor).
.suporte-card__chamada
  color: var(--neutral-600)

</style>
